import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from './components.js';
import { Icons } from './icons.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(node: HTMLElement): HTMLElement[] {
  return [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
}

export interface DialogProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'wide' | 'full';
  /** "alertdialog" for a question that needs an answer before going on (a confirmation) */
  role?: 'dialog' | 'alertdialog';
  /** the close button's accessible name */
  closeLabel: string;
  /** a selector inside the dialog to focus first; otherwise the first field */
  initialFocus?: string;
  /** a click on the dim area closes it (off for forms, so typing is not lost) */
  dismissOnBackdrop?: boolean;
  /** the dialog draws its own header (and names itself with labelledBy) */
  bare?: boolean;
  labelledBy?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * A modal dialog: focus moves in and stays in (Tab wraps), Escape closes,
 * focus goes back where it was. Rendered at the end of <body>.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  closeLabel,
  initialFocus,
  dismissOnBackdrop = false,
  bare = false,
  labelledBy,
  className,
  'data-testid': testId,
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = ref.current;
    if (node) {
      const wanted =
        (initialFocus ? node.querySelector<HTMLElement>(initialFocus) : null) ??
        node.querySelector<HTMLElement>('[autofocus]') ??
        node.querySelector<HTMLElement>('.ui-dialog__body input:not([type="hidden"]):not([disabled]), .ui-dialog__body select, .ui-dialog__body textarea') ??
        node;
      wanted.focus();
    }
    document.body.classList.add('ui-scroll-lock');
    return () => {
      document.body.classList.remove('ui-scroll-lock');
      if (previous?.isConnected) previous.focus();
    };
    // the first render decides where focus goes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close.current();
      return;
    }
    if (event.key !== 'Tab' || !ref.current) return;
    const items = focusables(ref.current);
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="ui-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (dismissOnBackdrop && event.target === event.currentTarget) close.current();
      }}
    >
      <div
        ref={ref}
        className={['ui-dialog', size !== 'md' ? `ui-dialog--${size}` : '', className ?? ''].join(' ').trim()}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-testid={testId}
      >
        {bare ? (
          children
        ) : (
          <>
            <header className="ui-dialog__header">
              <h2 id={titleId} className="ui-dialog__title">
                {title}
              </h2>
              <IconButton label={closeLabel} icon={<Icons.close />} onClick={() => close.current()} data-testid="dialog-close" />
            </header>
            <div className="ui-dialog__body">{children}</div>
            {footer ? <footer className="ui-dialog__footer">{footer}</footer> : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  title: ReactNode;
  /** what happens, in plain words */
  children?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  closeLabel: string;
  /** "danger" for what cannot be undone */
  tone?: 'danger' | 'primary';
  busy?: boolean;
  /** shown above the buttons when the action failed */
  error?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
  'data-testid'?: string;
}

/**
 * The design system's "are you sure" (in place of the browser's confirm()):
 * a small dialog that says what will happen, with "Cancel" focused first so
 * Enter never deletes by accident.
 */
export function ConfirmDialog({ title, children, confirmLabel, cancelLabel, closeLabel, tone = 'danger', busy = false, error, onConfirm, onClose, 'data-testid': testId }: ConfirmDialogProps) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      closeLabel={closeLabel}
      size="sm"
      role="alertdialog"
      initialFocus="[data-confirm='cancel']"
      data-testid={testId ?? 'confirm-dialog'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-confirm="cancel" data-testid="confirm-cancel">
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} busy={busy} onClick={onConfirm} data-testid="confirm-ok">
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children ? <div className="ui-confirm__body">{children}</div> : null}
      {error}
    </Dialog>
  );
}
